import { Col, Input, Table, Typography, Row, Radio, Tag } from 'antd';
import { format } from 'date-fns';
import { ShipiumTag } from '~/components/shared/ShipiumTag';
import { Flex } from '../Flex';
import { useMemo, useState } from 'react';
import { searchFilter } from '~/utils/search-filter';
import { carrierRatingMap, getActiveCalendarEntry } from '../rateCards/carrierRateUtils';
import { capitalizeWords } from '~/utils/regex';
import type { ColumnsType } from 'antd/lib/table';
import type { PartnerCarrierRateSummary, PartnerCarrierRateSummaryEntry } from '~/services/partner-management-service';
const { Text } = Typography;

export const SelectCarrierRate = ({
  carrierRates,
  omitCarrierRateIds,
  validate = false,
  onlyCurrency,
  value,
  onChange,
  loading,
}: {
  carrierRates: PartnerCarrierRateSummary[];
  omitCarrierRateIds?: string[];
  onlyCurrency?: string;
  validate?: boolean;
  loading: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
}) => {
  const [selectionFilter, setSelectionFilter] = useState<'all' | 'valid' | 'selected'>('all');
  const [searchValue, setSearchValue] = useState('');

  const sortedCarrierRates = useMemo(() => {
    let validatedCarrierRates = carrierRates.map((carrierRate) => {
      const { error, disabled } = validate
        ? validateCarrierRate(carrierRate, onlyCurrency)
        : { error: undefined, disabled: false };
      return { ...carrierRate, error, disabled };
    });

    validatedCarrierRates.sort((a, b) => {
      if (a.disabled !== b.disabled) {
        return a.disabled ? 1 : -1;
      }

      return a.name.localeCompare(b.name);
    });

    if (selectionFilter === 'selected') {
      validatedCarrierRates = validatedCarrierRates.filter((carrierRate) => value?.includes(carrierRate.carrierRateId));
    }

    if (selectionFilter === 'valid') {
      validatedCarrierRates = validatedCarrierRates.filter((carrierRate) => !carrierRate.disabled);
    }

    if (searchValue) {
      validatedCarrierRates = validatedCarrierRates.filter((carrierRate) =>
        searchFilter(searchValue, [carrierRate.name, carrierRate.carrierServiceMethodId]),
      );
    }

    if (omitCarrierRateIds) {
      validatedCarrierRates = validatedCarrierRates.filter(
        (carrierRate) => !omitCarrierRateIds.includes(carrierRate.carrierRateId),
      );
    }

    return validatedCarrierRates;
  }, [carrierRates, selectionFilter, searchValue, omitCarrierRateIds, validate, onlyCurrency, value]);

  return (
    <Flex direction="vertical" gap="sm">
      <Row gutter={[32, 16]}>
        <Col sm={24} md={24} lg={24} xl={12}>
          <Input.Search
            allowClear
            placeholder="Search"
            width={50}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </Col>
        <Col sm={24} md={24} lg={24} xl={12}>
          <Flex direction="horizontal" justifyContent="flex-end">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={selectionFilter}
              onChange={(e) => setSelectionFilter(e.target.value as 'all' | 'valid' | 'selected')}
            >
              <Radio value="all">Show All</Radio>
              <Radio value="valid">Show Valid</Radio>
              <Radio value="selected">Show Selected</Radio>
            </Radio.Group>
          </Flex>
        </Col>
      </Row>
      <CarrierRateSelectTable
        loading={loading}
        carrierRates={sortedCarrierRates}
        validate={validate}
        onlyCurrency={onlyCurrency}
        value={value}
        onChange={onChange}
      />
    </Flex>
  );
};

export function CarrierRateSelectTable({
  carrierRates,
  validate = false,
  onlyCurrency,
  value,
  onChange,
  size = 'middle',
  className,
  type = 'checkbox',
  loading = false,
  readonly = false,
}: {
  carrierRates: PartnerCarrierRateSummary[];
  omitCarrierRateIds?: string[];
  validate?: boolean;
  onlyCurrency?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  type?: 'checkbox' | 'radio';
  loading: boolean;
  readonly?: boolean;
}) {
  const dataSource = carrierRates.map((carrierRate) => {
    const { error, disabled } = validate
      ? validateCarrierRate(carrierRate, onlyCurrency)
      : { error: undefined, disabled: false };
    return { ...carrierRate, error, disabled };
  });

  const columns: ColumnsType<(typeof dataSource)[number]> = [
    {
      title: 'Details',
      key: 'details',
      render: (_, record) => {
        return (
          <Flex direction="vertical" gap="xs">
            <Text disabled={record.disabled} strong>
              {record.name}
            </Text>
            <Text type="secondary" disabled={record.disabled}>
              {record.carrierServiceMethodId}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: 'Destination Type',
      dataIndex: 'destinationType',
      key: 'destinationType',
      width: 100,
      render: (destinationType: string) => {
        return <Tag>{capitalizeWords(destinationType ?? 'N/A')}</Tag>;
      },
    },
    {
      title: 'Calendar Entries',
      dataIndex: 'rateCalendar',
      key: 'rateCalendar',
      width: 200,
      render: (val: PartnerCarrierRateSummaryEntry[], record) => {
        return <DateRangeTags rateCalendars={val} disabled={record.disabled} />;
      },
    },
    {
      title: 'Active Rating Type',
      key: 'ratingType',
      width: 100,
      render: (_, record) => {
        const { activeRateEntry } = getActiveCalendarEntry(record.rateCalendar);
        return <Tag>{activeRateEntry ? carrierRatingMap[activeRateEntry.ratingType ?? 'UNCOSTED'] : 'N/A'}</Tag>;
      },
    },
    {
      title: 'Active Surcharge Modifiers',
      width: 100,
      key: 'activeSurchargeModifiers',
      render: (_, record) => {
        const activeEntry = getActiveCalendarEntry(record.rateCalendar);
        return (
          <Flex direction="vertical" alignItems="center">
            <Text disabled={record.disabled}>{activeEntry?.activeRateEntry?.negotiatedSurchargesCount ?? 'N/A'}</Text>
          </Flex>
        );
      },
    },
  ];

  if (validate) {
    columns.push({
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        return <RenderValidationStatus error={record.error} carrierRate={record} />;
      },
    });
  }

  return (
    <Table
      loading={loading}
      size={size}
      className={className}
      rowKey="carrierRateId"
      rowSelection={
        readonly
          ? undefined
          : {
              type: type,
              selectedRowKeys: value,
              onChange(selectedRowKeys) {
                onChange?.(selectedRowKeys as string[]);
              },
              getCheckboxProps: (record) => ({
                disabled: record.disabled,
              }),
            }
      }
      columns={columns}
      dataSource={dataSource}
    />
  );
}

function validateCarrierRate(
  carrierRate: PartnerCarrierRateSummary | undefined,
  onlyCurrency: string | undefined,
): { error?: string; disabled: boolean } {
  const upperCaseOnlyCurrency = onlyCurrency?.toUpperCase();
  if (!carrierRate || !carrierRate.active || carrierRate.archived) {
    return { error: 'Inactive', disabled: true };
  }

  const upperCaseCarrierRateCurrency = carrierRate.currency?.toUpperCase() ?? 'USD';
  if (upperCaseOnlyCurrency && upperCaseCarrierRateCurrency !== upperCaseOnlyCurrency) {
    return { error: `Currency Mismatch: ${upperCaseCarrierRateCurrency}`, disabled: true };
  }

  return { error: undefined, disabled: false };
}

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const westernTimezones = ['America/Los_Angeles', 'America/Vancouver', 'America/Tijuana'];
const isWesternTimezone = westernTimezones.includes(userTimezone);
// Display fix for western timezone users seeing previous day due to -1 second end date boundary
// added to address https://shipium.atlassian.net/browse/CUS-11692
// this should not be used for writes and is strictly to solve the
// specific display issue mentioned above
// This is only needed to for west coast users because we hard code 7:59 as the utc hours.
// For east and central users this already bumps correctly to the next day
const addTwoSecondsForWesternTimezoneDisplay = (dateString: string): Date => {
  const date = new Date(dateString);
  if (isWesternTimezone) {
    date.setSeconds(date.getSeconds() + 2);
  }
  return date;
};

export const DateRangeTags = ({
  rateCalendars,
  disabled,
}: {
  rateCalendars: PartnerCarrierRateSummaryEntry[];
  disabled: boolean;
}) => {
  const { activeRateEntry } = getActiveCalendarEntry(rateCalendars);
  const sortedRateCalendars = rateCalendars.sort(
    (a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime(),
  );

  return (
    <Flex direction="horizontal" gap="xs" wrap>
      {sortedRateCalendars.slice(0, 2).map((rate) => {
        return (
          <ShipiumTag color={disabled || !activeRateEntry ? 'Grey' : 'Teal'} key={rate.startDateTime}>
            {format(new Date(rate.startDateTime), 'MMM dd, yyyy')} -{' '}
            {format(addTwoSecondsForWesternTimezoneDisplay(rate.endDateTime), 'MMM dd, yyyy')}
          </ShipiumTag>
        );
      })}
      {sortedRateCalendars.length > 2 && (
        <ShipiumTag color={disabled || !activeRateEntry ? 'Grey' : 'Teal'}>
          And {sortedRateCalendars.length - 2} more
        </ShipiumTag>
      )}
    </Flex>
  );
};

const RenderValidationStatus = ({ error, carrierRate }: { error?: string; carrierRate: PartnerCarrierRateSummary }) => {
  if (!error) {
    return <ShipiumTag color="Blue">Currency: {carrierRate.currency?.toUpperCase() ?? 'USD'}</ShipiumTag>;
  }

  return <ShipiumTag color="Coral">{error}</ShipiumTag>;
};
